import { useEffect, useState } from 'react';
import {
  Alert, Autocomplete, Box, Button, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle,
  Divider, Snackbar, SnackbarCloseReason, TextField, Typography,
} from '@mui/material';
import { useDebounce } from '../../../shared/hooks';
import { ContaBancariaService } from '../../../shared/services/api/cadastros/ContaBancaria';
import { SessaoCaixaService, ISessaoCaixaAtiva } from '../../../shared/services/api/Financeiro/SessaoCaixa';

export interface DialogAbrirSessaoCaixaProps {
  open: boolean;
  onClose: (sessao?: ISessaoCaixaAtiva) => void;
}

type TContaOpcao = { idconta: string; label: string };

const formatMoeda = (valor: number) =>
  Number(valor || 0).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

// Escolha do caixa a trabalhar + abertura da sessão. Só lista contas
// `tipo='caixa'` (mesmo filtro do backend em abrirSessao — aqui é só pra não
// oferecer opção inválida na tela, a validação de verdade é no backend).
// Abertura é sempre em Dinheiro (mesma regra do fechamento — PIX/Cartão são
// roteados direto pras contas de recebíveis, nunca "estão" no caixa físico)
// e aceita valor 0 (mesma regra já existente pro PDV).
export const DialogAbrirSessaoCaixa: React.FC<DialogAbrirSessaoCaixaProps> = ({ open, onClose }) => {
  const { debounce } = useDebounce();

  const [busca, setBusca] = useState('');
  const [opcoesConta, setOpcoesConta] = useState<TContaOpcao[]>([]);
  const [carregandoContas, setCarregandoContas] = useState(false);
  const [contaSelecionada, setContaSelecionada] = useState<TContaOpcao | null>(null);

  const [carregandoSaldo, setCarregandoSaldo] = useState(false);
  const [saldoAnterior, setSaldoAnterior] = useState<number | null>(null);
  const [saldoTotal, setSaldoTotal] = useState<number | null>(null);

  const [valorAberturaTexto, setValorAberturaTexto] = useState('');

  const [abrindo, setAbrindo] = useState(false);
  const [openmsg, setOpenmsg] = useState(false);
  const [msg, setMsg] = useState('');
  const [tms, setTms] = useState<'warning' | 'error' | 'info' | 'success'>('warning');

  useEffect(() => {
    if (!open) return;
    setBusca('');
    setContaSelecionada(null);
    setSaldoAnterior(null);
    setSaldoTotal(null);
    setValorAberturaTexto('');
  }, [open]);

  useEffect(() => {
    if (!open) return;
    setCarregandoContas(true);
    debounce(() => {
      ContaBancariaService.getAllList(busca, '', 'caixa', 20).then((resultado) => {
        setCarregandoContas(false);
        if (resultado instanceof Error) { setOpcoesConta([]); return; }
        setOpcoesConta(resultado.data.map((conta) => ({ idconta: conta.idconta, label: conta.titular })));
      });
    });
  }, [open, busca, debounce]);

  useEffect(() => {
    if (!contaSelecionada) {
      setSaldoAnterior(null);
      setSaldoTotal(null);
      return;
    }
    setCarregandoSaldo(true);
    SessaoCaixaService.getSaldoPreview(contaSelecionada.idconta).then((resultado) => {
      setCarregandoSaldo(false);
      if (resultado instanceof Error) {
        setSaldoAnterior(null);
        setSaldoTotal(null);
        return;
      }
      setSaldoAnterior(resultado.saldoanterior);
      setSaldoTotal(resultado.saldototal);
    });
  }, [contaSelecionada]);

  const valorAbertura = valorAberturaTexto ? Number(valorAberturaTexto) / 100 : 0;
  const valorAberturaExibido = valorAbertura === 0
    ? ''
    : valorAbertura.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  const handleChangeValorAbertura = (texto: string) => {
    setValorAberturaTexto(texto.replace(/\D/g, ''));
  };

  const handleCloseMsg = (event?: React.SyntheticEvent | Event, reason?: SnackbarCloseReason) => {
    if (reason === 'clickaway') return;
    setOpenmsg(false);
  };

  const confirmar = () => {
    if (!contaSelecionada) {
      setTms('warning');
      setMsg('Selecione o caixa em que você vai trabalhar');
      setOpenmsg(true);
      return;
    }

    setAbrindo(true);
    SessaoCaixaService
      .abrir(contaSelecionada.idconta, [{ categoria: 'Dinheiro', valor: valorAbertura }])
      .then((resultado) => {
        setAbrindo(false);
        if (resultado instanceof Error) {
          setTms('error');
          setMsg(resultado.message);
          setOpenmsg(true);
          return;
        }
        onClose(resultado);
      });
  };

  const handleCancel = () => onClose();

  return (
    <Dialog open={open} onClose={handleCancel} maxWidth="xs" fullWidth>
      <DialogTitle>Abrir sessão de caixa</DialogTitle>
      <DialogContent dividers>
        <Snackbar anchorOrigin={{ vertical: 'top', horizontal: 'center' }} open={openmsg} autoHideDuration={tms === 'error' ? null : 2500} onClose={handleCloseMsg}>
          <Alert onClose={handleCloseMsg} severity={tms} variant="filled" sx={{ width: '100%' }}>
            {msg}
          </Alert>
        </Snackbar>

        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
          Escolha o caixa que você vai trabalhar
        </Typography>
        <Autocomplete
          openText='Abrir'
          closeText='Fechar'
          noOptionsText='Nenhum caixa cadastrado'
          loadingText='Carregando...'
          disablePortal
          options={opcoesConta}
          loading={carregandoContas}
          disabled={abrindo}
          value={contaSelecionada}
          onInputChange={(_, newValue) => setBusca(newValue)}
          onChange={(_, newValue) => setContaSelecionada(newValue)}
          popupIcon={carregandoContas ? <CircularProgress size={20} /> : undefined}
          renderInput={(params) => (
            <TextField {...params} size='small' label='Caixa' />
          )}
          sx={{ mb: 2 }}
        />

        {contaSelecionada && (
          <Box sx={{ mb: 2 }}>
            {carregandoSaldo ? (
              <Typography variant="caption" color="text.secondary">Consultando saldo do caixa...</Typography>
            ) : saldoTotal !== null && (
              <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 2 }}>
                <Box>
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>Saldo anterior</Typography>
                  <Typography variant="body2" fontWeight={600}>R$ {formatMoeda(saldoAnterior || 0)}</Typography>
                </Box>
                <Box textAlign="right">
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>Saldo total do caixa</Typography>
                  <Typography variant="body2" fontWeight={700}>R$ {formatMoeda(saldoTotal)}</Typography>
                </Box>
              </Box>
            )}
          </Box>
        )}

        <Divider sx={{ mb: 2 }} />

        <TextField
          fullWidth
          size="small"
          label="Valor de abertura (Dinheiro)"
          placeholder="0,00"
          value={valorAberturaExibido}
          onChange={(e) => handleChangeValorAbertura(e.target.value)}
          inputProps={{ inputMode: 'numeric' }}
          disabled={abrindo}
        />
      </DialogContent>
      <DialogActions>
        <Button variant="outlined" color="error" onClick={handleCancel} disabled={abrindo}>Cancelar</Button>
        <Button variant="outlined" autoFocus disabled={abrindo} onClick={confirmar}>
          {abrindo ? 'Abrindo...' : 'Confirmar e abrir'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
